import { z } from 'zod';
import { WorkerTaintsSchema, findUnknownTaints, unknownTaintsMessage, } from '../workers/taints.js';
import { WorkerDisksSchema } from '../workers/disks.js';
// Build status enum
export const BuildStatusSchema = z.enum([
    'pending',
    'running',
    'success',
    'failed',
    'cancelling',
    'cancelled',
]);
// Build trigger source enum
export const TriggerSourceSchema = z.enum([
    'webhook', // GitLab webhook (push, merge_request)
    'ui', // Manual trigger from Kiln UI
    'api', // Direct API call
    'cli', // CLI tool
    'schedule', // Cron schedule trigger
]);
// Artifact info schema (forward declaration for Build)
const ArtifactInfoSchemaInternal = z.object({
    key: z.string(),
    size: z.number(),
    createdAt: z.coerce.date(),
});
// Stage artifact info schema (forward declaration for Build)
const StageArtifactInfoSchemaInternal = z.object({
    name: z.string(),
    stageName: z.string(),
    key: z.string(),
    size: z.number(),
    fileCount: z.number(),
    createdAt: z.coerce.date(),
});
// Build artifacts schema (forward declaration for Build)
const BuildArtifactsSchemaInternal = z.object({
    logs: ArtifactInfoSchemaInternal.nullable().optional(),
    stages: z.array(StageArtifactInfoSchemaInternal).optional(),
});
// Stage status enum (subset of build status + skipped + running)
export const StageStatusSchema = z.enum(['success', 'failed', 'skipped', 'running']);
// Stage result (used in API responses and final build status)
export const StageResultSchema = z.object({
    name: z.string().min(1),
    stageIndex: z.number().int().min(0),
    isFinally: z.boolean().default(false),
    status: StageStatusSchema,
    durationMs: z.number().int().min(0).optional(), // absent for running stages
    startedAt: z.coerce.date().optional(), // when the stage started running
    exitCode: z.number().optional(),
    errorMessage: z.string().optional(),
});
// Stage event (worker → master, sent as stages start/complete)
export const StageEventSchema = z.object({
    buildId: z.string().uuid(),
    name: z.string().min(1),
    stageIndex: z.number().int().min(0),
    isFinally: z.boolean().default(false),
    status: StageStatusSchema,
    durationMs: z.number().int().min(0).optional(),
    startedAt: z.coerce.date().optional(), // when the stage started running
    exitCode: z.number().optional(),
    errorMessage: z.string().optional(),
});
// Build schema
// Note: repo and organizationId are derived from repository (via join), not stored in builds table
export const BuildSchema = z.object({
    id: z.string().uuid(),
    repositoryId: z.string().uuid(),
    repositoryName: z.string().min(1), // Derived from repository.name
    repo: z.string().min(1), // Derived from repository.clone_url
    organizationId: z.string().uuid(), // Derived from repository.organization_id
    organizationSlug: z.string().min(1), // Derived from organization.slug
    branch: z.string().default('main'),
    pipelineName: z.string().nullable().optional(), // Which pipeline to run (null = default/first)
    status: BuildStatusSchema,
    workerId: z.string().nullable(),
    workerName: z.string().nullable().optional(), // Derived from worker.name (via join)
    exitCode: z.number().nullable(),
    errorMessage: z.string().nullable(),
    createdAt: z.coerce.date(),
    startedAt: z.coerce.date().nullable(),
    finishedAt: z.coerce.date().nullable(),
    commitSha: z.string().length(40).nullable(),
    eventType: z.string().nullable(),
    triggerSource: TriggerSourceSchema.default('webhook'), // Who triggered the build
    artifacts: BuildArtifactsSchemaInternal.nullable().optional(),
    // Worker-routing constraints stamped at build creation. Two bags:
    //   - `requires` (hard): every key/value must be present on a worker's
    //                        `labels` for that worker to be eligible.
    //   - `prefers`  (soft): union of `pipeline.labels.prefers` and
    //                        trigger metadata (git author, PR number,
    //                        project name, …). Drives ranking.
    labels: z
        .object({
        requires: z.record(z.string(), z.string()).default({}),
        prefers: z.record(z.string(), z.string()).default({}),
    })
        .default({ requires: {}, prefers: {} }),
    stages: z.array(StageResultSchema).optional(), // Per-stage timing/status data
    image: z.string().nullable().optional(),
});
// Ref type for manual builds (branch, tag, or pull_request)
export const RefTypeSchema = z.enum(['branch', 'tag', 'pull_request']);
// Build creation request
export const BuildRequestSchema = z.object({
    repositoryId: z.string().uuid(),
    branch: z.string().optional().default('main'),
    pipeline: z.string().optional(), // Specific pipeline to run
    refType: RefTypeSchema.optional(), // Type of ref being built (branch, tag, or pull_request)
});
// Workspace schema
export const WorkspaceSchema = z.object({
    id: z.string().uuid(),
    repo: z.string(),
    branch: z.string(),
    path: z.string(),
    workerId: z.string(),
    createdAt: z.coerce.date(),
    lastUsedAt: z.coerce.date(),
});
// Worker status
export const WorkerStatusSchema = z.enum(['pending', 'online', 'offline', 'busy']);
// Worker schema. Workers advertise what they *are* via `labels`
// (self-description); routing preferences live on the build. The one
// worker-side policy is `taints`: label keys whose `key=value` a build
// must explicitly require to be dispatched here (see workers/taints.ts).
export const WorkerSchema = z.object({
    id: z.string(),
    name: z.string().nullable(),
    hostname: z.string().nullable(),
    labels: z.record(z.string(), z.unknown()).default({}),
    taints: z.array(z.string()).default([]),
    maxConcurrentBuilds: z.number().default(1),
    activeBuilds: z.number().default(0),
    status: WorkerStatusSchema,
    createdBy: z.string().nullable(),
    createdAt: z.coerce.date(),
    registeredAt: z.coerce.date().nullable(),
    lastHeartbeatAt: z.coerce.date().nullable(),
    disabled: z.boolean().default(false),
    disabledReason: z.string().nullable(),
    disabledAt: z.coerce.date().nullable(),
    disabledBy: z.string().nullable(),
    // kiln version the worker/agent last reported in its heartbeat; null
    // until it heartbeats.
    version: z.string().nullable().default(null),
    // Latest disk usage reported in the worker's heartbeat (see
    // workers/disks.ts). Empty until a worker that reports it heartbeats.
    disks: WorkerDisksSchema.default([]),
    disksReportedAt: z.coerce.date().nullable().default(null),
});
// Payload for POST /api/workers/:id/disable. Reason is optional —
// operators can leave it empty for trivial maintenance.
export const WorkerDisableSchema = z.object({
    reason: z.string().trim().max(500).optional(),
});
// Worker creation request (admin creates worker, gets registration token)
// `taints` must be a subset of the keys of `labels`.
export const WorkerCreateSchema = z
    .object({
    name: z.string().min(1).max(255),
    maxConcurrentBuilds: z.number().min(1).default(1),
    labels: z.record(z.string(), z.unknown()).optional().default({}),
    taints: WorkerTaintsSchema.optional().default([]),
    tokenTtlMinutes: z.number().min(1).max(43200).optional().default(1440), // 24h default, max 30 days
})
    .superRefine((data, ctx) => {
    const unknown = findUnknownTaints(data.labels, data.taints);
    if (unknown.length > 0) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['taints'],
            message: unknownTaintsMessage(unknown),
        });
    }
});
// Worker creation response (includes one-time registration token)
export const WorkerCreatedSchema = z.object({
    worker: WorkerSchema,
    workerToken: z.string(), // Shown only once
    tokenExpiresAt: z.coerce.date(),
});
// Health check response
export const HealthStatusSchema = z.object({
    version: z.string(),
    status: z.enum(['ok', 'degraded', 'error']),
    postgres: z.object({
        connected: z.boolean(),
    }),
    oidc: z.object({
        connected: z.boolean(),
    }),
});
// Pagination
export const PaginationSchema = z.object({
    page: z.coerce.number().min(1).default(1),
    pageSize: z.coerce.number().min(1).max(100).default(20),
});
// User (from JWT claims)
export const UserRoleSchema = z.enum(['admin', 'developer', 'readonly']);
export const UserSchema = z.object({
    sub: z.string(),
    email: z.string().email().optional(),
    name: z.string().optional(),
    preferredUsername: z.string().optional(),
    roles: z.array(UserRoleSchema).default([]),
    groups: z.array(z.string()).default([]),
});
// ============================================
// Organization Types
// ============================================
// Organization schema
export const OrganizationSchema = z.object({
    id: z.string().uuid(),
    slug: z.string().min(1).max(63).regex(/^[a-z0-9-]+$/),
    name: z.string().min(1).max(255),
    description: z.string().nullable().optional(),
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
});
// Organization creation request
export const OrganizationCreateSchema = z.object({
    slug: z.string().min(1).max(63).regex(/^[a-z0-9-]+$/),
    name: z.string().min(1).max(255),
    description: z.string().nullable().optional(),
});
// Organization update request
export const OrganizationUpdateSchema = z.object({
    name: z.string().min(1).max(255).optional(),
    description: z.string().nullable().optional(),
});
// Git integration schema (provider-agnostic)
export const GitIntegrationSchema = z.object({
    id: z.string().uuid(),
    organizationId: z.string().uuid(),
    provider: z.enum(['gitlab', 'gitea']),
    providerUrl: z.string().url(),
    groupPath: z.string().nullable(),
    webhookSecret: z.string(),
    defaultBranch: z.string().default('main'),
    enabled: z.boolean().default(true),
    registryUrl: z.string().nullable(),
    createdAt: z.coerce.date(),
});
// Git integration config request (token is separate for security)
// All fields except the core ones are optional since they have defaults on the server
// accessToken is optional for updates (reuses existing token if not provided)
export const GitIntegrationConfigSchema = z.object({
    provider: z.enum(['gitlab', 'gitea']),
    providerUrl: z.string().url(),
    groupPath: z.string().min(1, 'Group/organization path is required'),
    accessToken: z.string().min(1).optional(),
    defaultBranch: z.string().optional(),
    enabled: z.boolean().optional(),
    registryUrl: z.string().min(1).optional(),
});
// Git provider types
export const GitProviderSchema = z.enum(['gitlab', 'gitea']);
// Repository schema (synced from git providers)
export const RepositorySchema = z.object({
    id: z.string().uuid(),
    organizationId: z.string().uuid(),
    provider: GitProviderSchema,
    providerRepoId: z.string(), // ID in the provider's system (as string for flexibility)
    name: z.string(),
    fullPath: z.string(),
    cloneUrl: z.string().url(),
    defaultBranch: z.string().default('main'),
    buildEnabled: z.boolean().default(false),
    providerWebhookId: z.string().nullable(), // Webhook ID in the provider's system
    podTemplateId: z.string().uuid().nullable(),
    preserveGitignore: z.boolean().default(false), // Preserve gitignored files (e.g., .emb/, node_modules/) between builds
    dockerRegistryEnabled: z.boolean().default(false), // Enable Docker registry credentials in builds
    packageRegistryEnabled: z.boolean().default(false), // Enable Package registry credentials in builds
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
});
// Repository update request
export const RepositoryUpdateSchema = z.object({
    buildEnabled: z.boolean().optional(),
    podTemplateId: z.string().uuid().nullable().optional(),
    preserveGitignore: z.boolean().optional(), // Preserve gitignored files between builds
    dockerRegistryEnabled: z.boolean().optional(), // Enable Docker registry credentials in builds
    packageRegistryEnabled: z.boolean().optional(), // Enable Package registry credentials in builds
});
// Pipeline schedule info (for API responses)
export const PipelineScheduleInfoSchema = z.object({
    id: z.string().uuid(),
    repositoryId: z.string().uuid(),
    repositoryName: z.string(),
    organizationSlug: z.string(),
    pipelineName: z.string(),
    cronExpression: z.string(),
    branch: z.string().nullable(),
    concurrency: z.string(),
    nextTrigger: z.coerce.date(),
    lastTriggered: z.coerce.date().nullable(),
    lastError: z.string().nullable(),
    lastErrorAt: z.coerce.date().nullable(),
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
});
// .kiln.yml config schema
export const KilnConfigSchema = z.object({
    commands: z.array(z.string()).optional(),
    branches: z.record(z.string(), z.object({
        commands: z.array(z.string()),
    })).optional(),
});
export const PaginatedSchema = (itemSchema) => z.object({
    items: z.array(itemSchema),
    total: z.number(),
    page: z.number(),
    pageSize: z.number(),
});
// Paginated builds response
export const PaginatedBuildsSchema = PaginatedSchema(BuildSchema);
// Paginated workspaces response
export const PaginatedWorkspacesSchema = PaginatedSchema(WorkspaceSchema);
// Paginated organizations response
export const PaginatedOrganizationsSchema = PaginatedSchema(OrganizationSchema);
// Organization with nested repos and integration (for mode=full)
export const OrganizationFullSchema = OrganizationSchema.extend({
    repositories: z.array(RepositorySchema),
    integration: GitIntegrationSchema.nullable(),
});
// Paginated organizations full response
export const PaginatedOrganizationsFullSchema = PaginatedSchema(OrganizationFullSchema);
// Paginated repositories response
export const PaginatedRepositoriesSchema = PaginatedSchema(RepositorySchema);
// ============================================
// Organization Authorization Helpers
// ============================================
// Organization role derived from group membership
export const OrgRoleSchema = z.enum(['admin', 'developer', 'member']);
// ============================================
// Master-Worker Protocol Types
// ============================================
// Worker registration request (worker → master)
// Worker claims pre-created identity using registration token in header
export const WorkerRegistrationSchema = z.object({
    hostname: z.string().optional(),
});
// Worker connect response (simplified - just worker ID)
export const WorkerConnectResponseSchema = z.object({
    workerId: z.string(),
});
// Worker heartbeat (worker → master)
// Worker ID is derived from session key in header
export const WorkerHeartbeatSchema = z.object({
    activeBuilds: z.number().default(0),
    runningBuildIds: z.array(z.string().uuid()).default([]),
    version: z.string().optional(),
    // Optional so older workers keep heartbeating; omitted = no update.
    disks: WorkerDisksSchema.optional(),
});
// Registry credentials for builds (Docker/Package registry access)
export const RegistryCredentialsSchema = z.object({
    registryUrl: z.string(), // e.g., registry.gitlab.com
    registryImage: z.string(), // e.g., registry.gitlab.com/group/project
    username: z.string(),
    password: z.string(),
    packageRegistryUrl: z.string().optional(), // Package registry URL (only if package registry enabled)
});
// Job assignment (master → worker)
export const BuildJobSchema = z.object({
    buildId: z.string().uuid(),
    repo: z.string(),
    branch: z.string(),
    pipelineName: z.string().nullable().optional(), // Which pipeline to run (null = default/first)
    commitSha: z.string().nullable().optional(),
    // Trigger-derived soft labels (git author, PR number, project, tag,
    // …) surfaced to the worker so it can derive build-context env vars
    // (`KILN_TAG`, `KILN_PULL_REQUEST`, `KILN_PROJECT`, …). Same bag as
    // `build.prefers` on master; the name `prefers` matches the DB
    // column.
    prefers: z.record(z.string()).optional(),
    preserveGitignore: z.boolean().optional(), // Preserve gitignored files (e.g., .emb/, node_modules/) between builds
    registryCredentials: RegistryCredentialsSchema.optional(), // Registry credentials for Docker/Package registry
});
// Heartbeat response (master → worker)
export const HeartbeatResponseSchema = z.object({
    job: BuildJobSchema.nullable(),
    cancelledBuildIds: z.array(z.string().uuid()).default([]),
});
// Build status update (worker → master)
export const BuildStatusUpdateSchema = z.object({
    buildId: z.string().uuid(),
    status: BuildStatusSchema,
    exitCode: z.number().optional(),
    errorMessage: z.string().optional(),
    startedAt: z.coerce.date().optional(),
    finishedAt: z.coerce.date().optional(),
    stages: z.array(StageResultSchema).optional(),
    image: z.string().optional(),
});
// Log line event (worker → master, for streaming)
export const LogLineSchema = z.object({
    buildId: z.string().uuid(),
    timestamp: z.coerce.date(),
    stream: z.enum(['stdout', 'stderr']),
    line: z.string(),
});
// Batch of log lines (worker → master, for batched streaming)
export const LogLinesSchema = z.object({
    buildId: z.string().uuid(),
    lines: z.array(z.object({
        timestamp: z.coerce.date(),
        stream: z.enum(['stdout', 'stderr']),
        line: z.string(),
    })),
});
// ============================================
// WebSocket Message Types
// ============================================
// WebSocket message type enum
export const WebSocketMessageTypeSchema = z.enum([
    'logs', // Batched log lines
    'status',
    'error',
    'subscribed',
    'unsubscribed',
    'history_start',
    'history_end',
    'ping',
    'pong',
]);
// Batched logs payload (multiple log lines for better performance)
export const WsLogsPayloadSchema = z.object({
    logs: z.array(LogLineSchema),
});
// Status change payload
export const WsStatusPayloadSchema = z.object({
    buildId: z.string().uuid(),
    status: BuildStatusSchema,
});
// Error payload
export const WsErrorPayloadSchema = z.object({
    error: z.string(),
});
// Subscribed payload
export const WsSubscribedPayloadSchema = z.object({
    buildId: z.string().uuid(),
});
// History start payload (marks beginning of log history replay)
export const WsHistoryStartPayloadSchema = z.object({
    buildId: z.string().uuid(),
    source: z.enum(['storage', 'buffer']),
});
// History end payload (marks end of log history replay)
export const WsHistoryEndPayloadSchema = z.object({
    buildId: z.string().uuid(),
});
// Generic WebSocket message
export const WebSocketMessageSchema = z.object({
    type: WebSocketMessageTypeSchema,
    payload: z.unknown().optional(),
});
// ============================================
// User Token Types (Personal Access Tokens)
// ============================================
// User token schema (what's returned from API, never includes full token)
export const UserTokenSchema = z.object({
    id: z.string().uuid(),
    userId: z.string(),
    name: z.string().min(1).max(255),
    tokenPrefix: z.string().length(8),
    expiresAt: z.coerce.date().nullable(),
    lastUsedAt: z.coerce.date().nullable(),
    createdAt: z.coerce.date(),
    revokedAt: z.coerce.date().nullable(),
});
// Token creation request
export const UserTokenCreateSchema = z.object({
    name: z.string().min(1).max(255),
    expiresInDays: z.number().min(1).max(365).nullable().optional(),
});
// Token creation response (includes full token, only returned once)
export const UserTokenCreatedSchema = z.object({
    token: UserTokenSchema,
    rawToken: z.string(), // The actual token value, shown only once
});
// Paginated tokens response
export const PaginatedUserTokensSchema = PaginatedSchema(UserTokenSchema);
// ============================================
// Build Artifacts Types
// ============================================
// Artifact type enum (logs or stage artifacts)
export const ArtifactTypeSchema = z.enum(['logs', 'stage']);
// Request for presigned upload URL (worker → master)
export const ArtifactUploadUrlRequestSchema = z.object({
    artifactType: ArtifactTypeSchema,
    // Required for stage artifacts
    stageName: z.string().optional(),
    artifactName: z.string().optional(),
});
// Response with presigned upload URL (master → worker)
export const ArtifactUploadUrlResponseSchema = z.object({
    uploadUrl: z.string().url(),
    key: z.string(),
});
// Single artifact info
export const ArtifactInfoSchema = z.object({
    key: z.string(),
    size: z.number(),
    createdAt: z.coerce.date(),
});
// When to collect stage artifacts
export const ArtifactWhenSchema = z.enum(['on_success', 'always', 'on_failure']).default('on_success');
// Stage artifact definition (in .kiln.yml)
export const StageArtifactDefinitionSchema = z.object({
    name: z.string().min(1, 'Artifact name is required'),
    globs: z.array(z.string().min(1)).min(1, 'At least one glob pattern is required'),
    when: ArtifactWhenSchema.optional(), // When to collect: on_success (default), always, on_failure
});
// Stage artifact info (uploaded artifact metadata)
export const StageArtifactInfoSchema = z.object({
    name: z.string(), // Name from definition
    stageName: z.string(), // Stage that produced it
    key: z.string(), // S3 key
    size: z.number(), // Size in bytes
    fileCount: z.number(), // Number of files in archive
    createdAt: z.coerce.date(),
});
// Confirm artifact upload (worker → master)
export const ArtifactConfirmRequestSchema = z.object({
    logs: ArtifactInfoSchema.optional(),
    stages: z.array(StageArtifactInfoSchema).optional(),
});
// Build artifacts (stored in database)
export const BuildArtifactsSchema = z.object({
    logs: ArtifactInfoSchema.nullable().optional(),
    stages: z.array(StageArtifactInfoSchema).optional(),
});
// ============================================
// Pod Template Types
// ============================================
// Toleration schema (Kubernetes toleration)
export const TolerationSchema = z.object({
    key: z.string().optional(),
    operator: z.enum(['Exists', 'Equal']).optional(),
    value: z.string().optional(),
    effect: z.enum(['NoSchedule', 'PreferNoSchedule', 'NoExecute']).optional(),
    tolerationSeconds: z.number().optional(),
});
// Volume schema (simplified Kubernetes volume)
export const VolumeSchema = z.object({
    name: z.string(),
    persistentVolumeClaim: z.object({
        claimName: z.string(),
        readOnly: z.boolean().optional(),
    }).optional(),
    secret: z.object({
        secretName: z.string(),
    }).optional(),
    configMap: z.object({
        name: z.string(),
    }).optional(),
    emptyDir: z.object({
        medium: z.string().optional(),
        sizeLimit: z.string().optional(),
    }).optional(),
});
// Volume mount schema
export const VolumeMountSchema = z.object({
    name: z.string(),
    mountPath: z.string(),
    readOnly: z.boolean().optional(),
    subPath: z.string().optional(),
});
// Environment variable schema
export const EnvVarSchema = z.object({
    name: z.string(),
    value: z.string().optional(),
    valueFrom: z.object({
        secretKeyRef: z.object({ name: z.string(), key: z.string() }).optional(),
        configMapKeyRef: z.object({ name: z.string(), key: z.string() }).optional(),
    }).optional(),
});
// EnvFrom schema
export const EnvFromSchema = z.object({
    secretRef: z.object({ name: z.string() }).optional(),
    configMapRef: z.object({ name: z.string() }).optional(),
    prefix: z.string().optional(),
});
// Security context schema
export const SecurityContextSchema = z.object({
    runAsUser: z.number().optional(),
    runAsGroup: z.number().optional(),
    runAsNonRoot: z.boolean().optional(),
    fsGroup: z.number().optional(),
});
// Docker-in-Docker sidecar configuration
export const DindConfigSchema = z.object({
    enabled: z.boolean().default(false),
    image: z.string().optional(), // Default: docker:27-dind
    // DNS servers for containers created by DinD (prevents K8s DNS conflicts)
    dns: z.array(z.string()).optional(), // Default: ["8.8.8.8", "8.8.4.4"]
    // DNS search domains (single dot clears K8s search domains like *.svc.cluster.local)
    dnsSearch: z.array(z.string()).optional(), // Default: ["."]
    resources: z.object({
        requests: z.object({
            cpu: z.string().optional(),
            memory: z.string().optional(),
        }).optional(),
        limits: z.object({
            cpu: z.string().optional(),
            memory: z.string().optional(),
        }).optional(),
    }).optional(),
});
// Pod template spec schema (the actual K8s-like spec)
export const PodTemplateSpecSchema = z.object({
    // Image configuration
    image: z.string().optional(),
    imagePullPolicy: z.enum(['Always', 'IfNotPresent', 'Never']).optional(),
    imagePullSecrets: z.array(z.string()).optional(),
    // Resources
    resources: z.object({
        requests: z.object({
            cpu: z.string().optional(),
            memory: z.string().optional(),
        }).optional(),
        limits: z.object({
            cpu: z.string().optional(),
            memory: z.string().optional(),
        }).optional(),
    }).optional(),
    // Scheduling
    nodeSelector: z.record(z.string()).optional(),
    tolerations: z.array(TolerationSchema).optional(),
    // Identity
    serviceAccountName: z.string().optional(),
    // Security
    securityContext: SecurityContextSchema.optional(),
    // Volumes
    volumes: z.array(VolumeSchema).optional(),
    volumeMounts: z.array(VolumeMountSchema).optional(),
    // Environment
    env: z.array(EnvVarSchema).optional(),
    envFrom: z.array(EnvFromSchema).optional(),
    // Labels and annotations for the pod
    labels: z.record(z.string()).optional(),
    annotations: z.record(z.string()).optional(),
    // Docker-in-Docker sidecar (for builds requiring docker)
    dind: DindConfigSchema.optional(),
});
// Pod template schema (the database entity)
export const PodTemplateSchema = z.object({
    id: z.string().uuid(),
    name: z.string().min(1).max(255),
    slug: z.string().regex(/^[a-z0-9-]+$/),
    description: z.string().nullable().optional(),
    spec: PodTemplateSpecSchema,
    isDefault: z.boolean(),
    isSystem: z.boolean(),
    createdBy: z.string().nullable(),
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
});
// Pod template creation request
export const PodTemplateCreateSchema = z.object({
    name: z.string().min(1).max(255),
    slug: z.string().regex(/^[a-z0-9-]+$/),
    description: z.string().optional(),
    spec: PodTemplateSpecSchema,
    isDefault: z.boolean().optional().default(false),
});
// Pod template update request
export const PodTemplateUpdateSchema = z.object({
    name: z.string().min(1).max(255).optional(),
    description: z.string().nullable().optional(),
    spec: PodTemplateSpecSchema.optional(),
    isDefault: z.boolean().optional(),
});
// ============================================
// Repository Refs Types (Branches, Tags, Pull Requests)
// ============================================
// Commit info (shared by branches and tags)
export const CommitInfoSchema = z.object({
    id: z.string(),
    shortId: z.string(),
    title: z.string(),
    authorName: z.string(),
    date: z.string(),
});
// Branch info
export const BranchInfoSchema = z.object({
    name: z.string(),
    commit: CommitInfoSchema,
    default: z.boolean(),
    protected: z.boolean(),
    webUrl: z.string(),
});
// Tag info
export const TagInfoSchema = z.object({
    name: z.string(),
    commit: CommitInfoSchema.omit({ authorName: true }),
    message: z.string().nullable(),
    webUrl: z.string(),
});
// Pull request / merge request author
export const PullRequestAuthorSchema = z.object({
    username: z.string(),
    name: z.string(),
});
// Pull request / merge request info
export const PullRequestInfoSchema = z.object({
    id: z.number(),
    number: z.number(), // iid for GitLab, number for Gitea
    title: z.string(),
    sourceBranch: z.string(),
    targetBranch: z.string(),
    state: z.string(),
    author: PullRequestAuthorSchema,
    webUrl: z.string(),
    createdAt: z.string(),
});
// Pipeline trigger configuration for UI display
export const PipelineTriggerInfoSchema = z.object({
    push: z.object({
        branches: z.array(z.string()).optional(),
        'branches-ignore': z.array(z.string()).optional(),
    }).nullable().optional(),
    pull_request: z.object({
        branches: z.array(z.string()).optional(),
        'branches-ignore': z.array(z.string()).optional(),
    }).nullable().optional(),
    tag: z.object({
        tags: z.array(z.string()).optional(),
        'tags-ignore': z.array(z.string()).optional(),
    }).nullable().optional(),
});
// Pipeline info for UI display
// Multiple trigger types in the object are OR logic - the pipeline runs if any trigger matches
export const PipelineInfoSchema = z.object({
    name: z.string(),
    on: PipelineTriggerInfoSchema.nullable().optional(),
    wouldRun: z.boolean(), // Whether this pipeline would run for the selected ref
    runReason: z.string().optional(), // Why it would/wouldn't run (e.g., "matches push trigger for branch 'main'")
    image: z.string().optional(), // Container image from `image:` in .kiln.yml (task 37 phase 3)
});
// Response for fetching pipelines at a ref
export const PipelinesAtRefResponseSchema = z.object({
    pipelines: z.array(PipelineInfoSchema),
    configExists: z.boolean(),
    error: z.string().optional(),
});
// ============================================
// Utility Functions
// ============================================
// UUID v4 regex pattern
const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
/**
 * Check if a string is a valid UUID v4 format
 */
export function isValidUUID(str) {
    return UUID_REGEX.test(str);
}
//# sourceMappingURL=index.js.map